import React, { FC, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import {
  ColumnReferenceCell,
  ColumnReferenceColumnCell,
  ColumnReferenceHead,
  ColumnReferenceTable,
  EffectParticipantsExampleTable,
  ExampleTableCell,
  ExampleTableHead,
  ExampleTableRow,
  GuideBackdrop,
  GuideBody,
  GuideCloseButton,
  GuideFooter,
  GuideHeader,
  GuideKickerLabel,
  GuideKickerRow,
  GuideModal,
  GuidePrimaryButton,
  GuideSubtitle,
  GuideSurveyBadge,
  GuideTitle,
  InfoCallout,
  InfoCalloutContent,
  InfoCalloutIcon,
  InfoCalloutList,
  InfoCalloutText,
  InfoCalloutTitle,
  MoreButton,
  MoreInlineCode,
  MorePanel,
  ParticipantsGuideSection,
  ParticipantsSectionTitle,
  RequiredYes,
  SectionDescription,
  VisualLabel,
} from './ImportModal.styles';
import CheckMark from '@/assets/icons/checkMark.svg?react';
import CloseMark from '@/assets/icons/closeMark.svg?react';

interface IImportParticipantsEffectHelpModal {
  active: boolean;
  onClose: () => void;
}

const COLUMN_ROW_IDS = [
  'email',
  'departments',
  'name',
  'phone',
  'language',
  'leader',
  'team',
  'role',
  'effectRole',
  'individualReportGroup',
  'backgroundVariables',
  'personalizations',
] as const;

const FILE_EXAMPLE_ROW_IDS = ['anna', 'erik', 'mia', 'ola'] as const;

const FILE_EXAMPLE_FIELDS = ['email', 'name', 'department1', 'effectRole', 'language'] as const;

const INFO_CALLOUT_IDS = ['emailKey', 'skipRow', 'departments'] as const;

type RequiredType = 'yes' | 'no' | 'atLeastOne';

const REQUIRED_TYPES: Record<typeof COLUMN_ROW_IDS[number], RequiredType> = {
  email: 'yes',
  departments: 'atLeastOne',
  name: 'no',
  phone: 'no',
  language: 'no',
  leader: 'no',
  team: 'no',
  role: 'no',
  effectRole: 'yes',
  individualReportGroup: 'no',
  backgroundVariables: 'no',
  personalizations: 'no',
};

const ImportParticipantsEffectHelpModal: FC<IImportParticipantsEffectHelpModal> = ({
  active,
  onClose,
}) => {
  const { t } = useTranslation();
  const [showMore, setShowMore] = useState(false);

  const renderRequired = (requiredType: RequiredType) => {
    if (requiredType === 'yes') {
      return (
        <RequiredYes>
          {t('imports.participantsHelp.effect.columnReference.requiredYes')}
        </RequiredYes>
      );
    }

    if (requiredType === 'atLeastOne') {
      return t('imports.participantsHelp.effect.columnReference.requiredAtLeastOne');
    }

    return t('imports.participantsHelp.effect.columnReference.requiredNo');
  };

  if (!active) return null;

  return (
    <>
      <GuideBackdrop onClick={onClose} />
      <GuideModal
        role="dialog"
        aria-modal="true"
        aria-labelledby="participants-effect-guide-title"
        data-testid="import-participants-effect-help-content"
      >
        <GuideHeader>
          <GuideKickerRow>
            <GuideKickerLabel>{t('imports.participantsHelp.effect.kicker')}</GuideKickerLabel>
            <GuideSurveyBadge>{t('imports.participantsHelp.effect.badge')}</GuideSurveyBadge>
          </GuideKickerRow>
          <GuideCloseButton type="button" aria-label={t('imports.close')} onClick={onClose}>
            <CloseMark />
          </GuideCloseButton>
          <GuideTitle id="participants-effect-guide-title">
            {t('imports.participantsHelp.effect.guideTitle')}
          </GuideTitle>
          <GuideSubtitle>{t('imports.participantsHelp.effect.guideSubtitle')}</GuideSubtitle>
        </GuideHeader>

        <GuideBody>
          <ParticipantsGuideSection>
            <ParticipantsSectionTitle>
              {t('imports.participantsHelp.effect.columnReference.title')}
            </ParticipantsSectionTitle>
            <SectionDescription>
              <Trans
                i18nKey="imports.participantsHelp.effect.columnReference.intro"
                components={[
                  <MoreInlineCode key="email" />,
                  <MoreInlineCode key="departmentN" />,
                  <MoreInlineCode key="effectRole" />,
                ]}
              />
            </SectionDescription>

            <ColumnReferenceTable>
              <thead>
                <tr>
                  <ColumnReferenceHead>
                    {t('imports.participantsHelp.effect.columnReference.headers.column')}
                  </ColumnReferenceHead>
                  <ColumnReferenceHead>
                    {t('imports.participantsHelp.effect.columnReference.headers.required')}
                  </ColumnReferenceHead>
                  <ColumnReferenceHead>
                    {t('imports.participantsHelp.effect.columnReference.headers.acceptedNames')}
                  </ColumnReferenceHead>
                  <ColumnReferenceHead>
                    {t('imports.participantsHelp.effect.columnReference.headers.notes')}
                  </ColumnReferenceHead>
                </tr>
              </thead>
              <tbody>
                {COLUMN_ROW_IDS.map(rowId => (
                  <tr key={rowId}>
                    <ColumnReferenceColumnCell>
                      {t(`imports.participantsHelp.effect.columnReference.rows.${rowId}.column`)}
                    </ColumnReferenceColumnCell>
                    <ColumnReferenceCell>
                      {renderRequired(REQUIRED_TYPES[rowId])}
                    </ColumnReferenceCell>
                    <ColumnReferenceCell>
                      {t(
                        `imports.participantsHelp.effect.columnReference.rows.${rowId}.acceptedNames`
                      )}
                    </ColumnReferenceCell>
                    <ColumnReferenceCell>
                      {t(`imports.participantsHelp.effect.columnReference.rows.${rowId}.notes`, {
                        defaultValue: '',
                      })}
                    </ColumnReferenceCell>
                  </tr>
                ))}
              </tbody>
            </ColumnReferenceTable>

            <VisualLabel>{t('imports.participantsHelp.effect.fileExample.title')}</VisualLabel>
            <EffectParticipantsExampleTable>
              <thead>
                <ExampleTableRow>
                  {FILE_EXAMPLE_FIELDS.map(field => (
                    <ExampleTableHead key={field}>
                      {t(`imports.participantsHelp.effect.fileExample.headers.${field}`)}
                    </ExampleTableHead>
                  ))}
                </ExampleTableRow>
              </thead>
              <tbody>
                {FILE_EXAMPLE_ROW_IDS.map(rowId => (
                  <ExampleTableRow key={rowId}>
                    {FILE_EXAMPLE_FIELDS.map(field => (
                      <ExampleTableCell key={`${rowId}-${field}`}>
                        {t(`imports.participantsHelp.effect.fileExample.rows.${rowId}.${field}`)}
                      </ExampleTableCell>
                    ))}
                  </ExampleTableRow>
                ))}
              </tbody>
            </EffectParticipantsExampleTable>

            <MoreButton
              type="button"
              aria-expanded={showMore}
              onClick={() => setShowMore(value => !value)}
            >
              <span>{showMore ? '−' : '+'}</span>
              {showMore
                ? t('imports.reportingGroupsHelp.hide')
                : t('imports.participantsHelp.effect.showMore')}
            </MoreButton>
            {showMore ? (
              <MorePanel>
                <InfoCalloutList>
                  {INFO_CALLOUT_IDS.map(calloutId => (
                    <InfoCallout key={calloutId}>
                      <InfoCalloutIcon aria-hidden="true">
                        <CheckMark />
                      </InfoCalloutIcon>
                      <InfoCalloutContent>
                        <InfoCalloutTitle>
                          {t(`imports.participantsHelp.effect.more.${calloutId}.title`)}
                        </InfoCalloutTitle>
                        <InfoCalloutText>
                          {calloutId === 'departments' ? (
                            <Trans
                              i18nKey={`imports.participantsHelp.effect.more.${calloutId}.text`}
                              components={{ strong: <strong /> }}
                            />
                          ) : (
                            t(`imports.participantsHelp.effect.more.${calloutId}.text`)
                          )}
                        </InfoCalloutText>
                      </InfoCalloutContent>
                    </InfoCallout>
                  ))}
                </InfoCalloutList>
              </MorePanel>
            ) : null}
          </ParticipantsGuideSection>
        </GuideBody>

        <GuideFooter>
          <GuidePrimaryButton type="button" onClick={onClose}>
            {t('imports.reportingGroupsHelp.gotIt')}
          </GuidePrimaryButton>
        </GuideFooter>
      </GuideModal>
    </>
  );
};

export default ImportParticipantsEffectHelpModal;
